import React, { FC, useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Avatar,
    Typography,
    Box,
    CircularProgress,
    TextField,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { GeneralQuery } from '../../../services/query';

interface IProps {
    open: boolean;
    onClose: () => void;
    countryIds: number[];
}

export const SCUICountryModal: FC<IProps> = ({ open, onClose, countryIds }) => {
    const { t } = useTranslation();
    const [countries, setCountries] = useState<any[]>([]);
    const [filteredCountries, setFilteredCountries] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (open && countryIds.length > 0) {
            getAllActiveCountryLanguagesForBackoffice();
        }
    }, [open, countryIds]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredCountries(countries);
        } else {
            const filtered = countries.filter((country) => {
                const countryName = getCountryName(country).toLowerCase();
                const countryCode = country.countryCode?.toLowerCase() || '';
                const search = searchTerm.toLowerCase();

                return countryName.includes(search) || countryCode.includes(search);
            });
            setFilteredCountries(filtered);
        }
    }, [searchTerm, countries]);

    useEffect(() => {
        if (!open) {
            setSearchTerm('');
        }
    }, [open]);

    const getAllActiveCountryLanguagesForBackoffice = () => {
        setLoading(true);
        const instanceId = parseInt(import.meta.env.VITE_INSTANCEID || '1');
        GeneralQuery.GetAllActiveCountryLanguagesForBackoffice(instanceId)
            .then((res: any) => {
                if (res) {
                    const allCountries = res.data?.boSupportedCountries || [];
                    // Filter countries by the provided IDs
                    const filteredCountries = allCountries.filter((country: any) =>
                        countryIds.includes(country.countryId)
                    );
                    // Sort countries by name
                    const sortedCountries = filteredCountries.sort((a: any, b: any) =>
                        a.countryName.localeCompare(b.countryName)
                    );
                    setCountries(sortedCountries);
                    setFilteredCountries(sortedCountries);
                }
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const getCountryName = (country: any) => {
        if (country.translations && typeof country.translations === 'object') {
            const currentLang = localStorage.getItem('i18nextLng') || 'tr-TR';
            if (country.translations[currentLang]) {
                return country.translations[currentLang];
            }
            const shortLang = currentLang.split('-')[0];
            if (country.translations[shortLang]) {
                return country.translations[shortLang];
            }
        }
        return country.countryName || country.countryCode;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        maxHeight: '70vh',
                        borderRadius: '12px',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    textAlign: 'center',
                    pb: 2,
                    pt: 3,
                    borderBottom: '1px solid #eee',
                    background: '#fafbfc',
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {t('menuManagement.countryInformation')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                    {loading
                        ? t('menuManagement.countryCount', { count: countryIds.length })
                        : t('menuManagement.countryCount', { count: filteredCountries.length })}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
                {!loading && (
                    <Box sx={{ mb: 3, mt: 1 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Ülke kodu"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#666' }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '8px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                )}
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '200px',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <CircularProgress size={40} sx={{ color: '#1976d2' }} />
                        <Typography variant="body2" sx={{ color: '#666' }}>
                            {t('general.loading') || 'Yükleniyor...'}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {filteredCountries.map((country) => (
                            <Grid
                                key={country.countryId}
                                size={{
                                    xs: 12,
                                    sm: 6,
                                    md: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        p: 2,
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#fafafa',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: '#f0f0f0',
                                            borderColor: '#1976d2',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                >
                                    <Avatar
                                        alt={country.countryCode}
                                        src={
                                            country.flagUrl ||
                                            country.flag ||
                                            `https://flagcdn.com/w40/${country.countryCode?.toLowerCase()}.png`
                                        }
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                                            border: '1px solid #e0e0e0',
                                            backgroundColor: '#f5f5f5',
                                        }}
                                    >
                                        {country.countryCode?.substring(0, 2)?.toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                color: '#333',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {getCountryName(country)}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#666',
                                                fontSize: '11px',
                                                fontWeight: 400,
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {country.countryCode}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        minWidth: 120,
                        fontWeight: 600,
                        borderRadius: '8px',
                        textTransform: 'none',
                        color: '#1976d2',
                        borderColor: '#1976d2',
                        backgroundColor: 'white',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#1565c0',
                            color: '#1565c0',
                        },
                    }}
                >
                    {t('general.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
